import {
  createContext,
  use,
  useEffect,
  useId,
  useRef,
  useSyncExternalStore,
  type ComponentProps,
  type PropsWithChildren,
  type ReactNode,
  type ReactEventHandler,
} from "react";
import { cn } from "#/classname.ts";
import { Button } from "#/components/button.tsx";
import { Box, Columns, Row } from "#/components/layout.tsx";

const DialogIdContext = createContext("");

export function Dialog(props: PropsWithChildren<{ id?: string }>) {
  const id = useId();
  // If no explicit ID passed then use randomly generated one
  return <DialogIdContext value={props.id ?? id} {...props} />;
}

Dialog.Open = Open;
Dialog.Close = Close;
Dialog.Content = Content;
Dialog.Form = Form;
Dialog.Title = Title;
Dialog.Description = Description;

function useDialogId() {
  const id = use(DialogIdContext);
  if (!id) throw new Error("<Dialog*> must render within <DialogContext>");
  return id;
}

const sizes = {
  default: "",
  sm: "w-md",
  md: "w-lg",
  lg: "w-2xl",
  xl: "w-4xl",
};

const positions = {
  center: "rounded-3xl shadow-lg",
  right: "rounded-l-3xl left-auto h-full shadow-2xl",
};

type DialogProps = PropsWithChildren<{
  /** Image to render in the left column on wider viewports */
  image?: string;
  /** Defaults to "any", allowing click outside to close. Set to "none" to force user action */
  closedby?: "any" | "closerequest" | "none" | undefined;
  /** IDs are autogenerated. If you need to control the ID set it on `<DialogContext>` */
  id?: never;
  /** Fix the dialog's size. It will still shrink when the viewport is smaller */
  size?: keyof typeof sizes;
  /** Place the dialog on the page. Entry/exit transitions match */
  position?: keyof typeof positions;
  p?: ComponentProps<typeof Box>["p"];
  /** For edge-case styling—this should be used very rarely */
  className?: string | undefined;
}> &
  (
    | { open?: never; onClose?: never }
    | {
        /** Only use if you need something other than `<DialogOpen>` to control */
        open: boolean;
        /** Runs when the dialog closes */
        onClose: ReactEventHandler<HTMLDialogElement> | undefined;
      }
  );

/**
 * Creates a modal dialog on top of the page content.
 * For when you need the user to complete a single task before continuing.
 * Don't use as a replacement for actual routable pages.
 * @example
 * <DialogContext>
 *  <DialogOpen>Create thing</DialogOpen>
 *  <Dialog image={imageSrc}>...</Dialog>
 * </DialogContext>
 */
function Content(props: DialogProps) {
  const {
    open,
    onClose,
    className,
    image,
    closedby = "any",
    size = "default",
    position = "center",
    p = "xl",
    children,
  } = props;
  const ref = useRef<HTMLDialogElement>(null);

  // Native `open` prop cannot trigger a modal dialog
  useEffect(() => {
    if (open && !ref.current?.open) ref.current?.showModal();
    else if (!open && ref.current?.open) ref.current?.close();
  }, [open]);

  const id = useDialogId();

  return (
    <dialog
      ref={ref}
      id={id}
      className={cn(className, "bg-white", positions[position], sizes[size])}
      aria-labelledby={id + "-title"}
      aria-describedby={id + "-desc"}
      closedby={closedby}
      onClose={onClose}
      transition={position === "right" ? "from-right" : "fade"}
    >
      {closedby === "any" && (
        <Dialog.Close
          icon={X}
          label="Close"
          className="absolute top-2 right-2"
        />
      )}
      <Columns p={p} gap="2xl">
        {image && (
          <img
            src={image}
            alt=""
            width={400}
            height={400}
            className="rounded-2xl object-cover aspect-square hidden lg:block"
          />
        )}
        <Box justify="center">{children}</Box>
      </Columns>
    </dialog>
  );
}

type ButtonProps = ComponentProps<typeof Button>;

/** Opens the <Dialog> rendered within the same <DialogContext> */
function Open(props: ButtonProps) {
  const id = useDialogId();
  return (
    <Button {...props} type="button" command="show-modal" commandFor={id} />
  );
}

/** Automatically closes parent <Dialog> after action completes */
function Form(props: ComponentProps<"form">) {
  const { action, children } = props;
  const id = useDialogId();
  return (
    <form
      action={
        typeof action === "string"
          ? action
          : async (data) => {
              await action?.(data);
              const dialog = document.getElementById(id);
              if (dialog instanceof HTMLDialogElement) dialog.close();
            }
      }
    >
      {children}
    </form>
  );
}

/** Accessibly labels parent <Dialog> with title */
function Title({ children }: PropsWithChildren) {
  const id = useDialogId();
  return (
    <h1 className="h2" id={id + "-title"}>
      {children}
    </h1>
  );
}

/** Accessibly connects description text to parent <Dialog> */
function Description(props: ComponentProps<"p">) {
  const id = useDialogId();
  return <p {...props} id={id + "-desc"} />;
}

/** Closes its parent <Dialog> */
function Close(props: ButtonProps) {
  const id = useDialogId();
  return (
    <Button
      theme="text"
      {...props}
      type="button"
      // So spinner doesn't show during action
      loading={false}
      command="close"
      commandFor={id}
    />
  );
}

type BaseOptions = {
  /** Control the theme of the confirm button */
  theme?: "primary" | "error";
  /** Image to render in the left column on wider viewports */
  image?: string;
  /** Title for the dialog */
  title: string;
  /** Description for the dialog */
  description?: string;
  /** Label for the confirm button */
  actionLabel?: string;
  /** Label for the cancel button */
  cancelLabel?: string;
  /** Fix the dialog's size. It will still shrink when the viewport is smaller */
  size?: keyof typeof sizes;
};

type ConfirmOptions = BaseOptions & { fields?: never };

type PromptOptions = BaseOptions & {
  /** JSX form fields to render inside the dialog */
  fields: ReactNode;
};

type Request = {
  options: ConfirmOptions | PromptOptions;
  resolve: (data: FormData | null) => void;
};

let current: Request | null = null;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

function setCurrent(next: Request | null) {
  // Only one dialog at a time, so resolve previous before opening next
  if (next) current?.resolve(null);
  current = next;
  for (const listener of listeners) listener();
}

/**
 * Triggers a prompt dialog with form fields.
 * Promise resolves with the submitted FormData, or null if cancelled.
 * @example
 * const data = await prompt({ title: "Invite someone", fields: <Field>…</Field> });
 */
export function prompt(options: PromptOptions) {
  return new Promise<FormData | null>((resolve) => {
    setCurrent({ options, resolve });
  });
}

/**
 * Triggers a confirmation dialog. Promise resolves with true/false
 * @example
 * const confirmed = await confirm({ title: "Delete this package?" }));
 */
export function confirm(options: ConfirmOptions) {
  return new Promise<boolean>((resolve) => {
    setCurrent({ options, resolve: (data) => resolve(data !== null) });
  });
}

/**
 * Render only once at the app root. This is triggered by prompt/confirm
 */
export function DialogRoot() {
  const request = useSyncExternalStore(subscribe, () => current);
  if (!request) return null;
  const {
    theme,
    image,
    title,
    description,
    fields,
    actionLabel = "Confirm",
    cancelLabel = "Cancel",
    size,
  } = request.options;
  return (
    <Dialog>
      <Dialog.Content
        className="Prompt"
        image={image}
        closedby="closerequest"
        open
        size={size}
        onClose={() => {
          // onClose means user cancelled, so clear the dialog
          request.resolve(null);
          setCurrent(null);
        }}
      >
        <Dialog.Form action={(data) => request.resolve(data)}>
          <Box>
            <Dialog.Title>{title}</Dialog.Title>
            {description && (
              <Dialog.Description className="max-w-xl">
                {description}
              </Dialog.Description>
            )}
            {fields}
            <Row justify="end">
              <Dialog.Close>{cancelLabel}</Dialog.Close>
              {/* prompts close immediately, so avoid flash of spinner */}
              <Button theme={theme} loading={false}>
                {actionLabel}
              </Button>
            </Row>
          </Box>
        </Dialog.Form>
      </Dialog.Content>
    </Dialog>
  );
}

const X = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
